import moment from 'moment';
import {Moment} from 'moment';

/** `strict` has no custom ranges and `day` is always a single day */
export type DateRangeMode = 'default' | 'strict' | 'day';

export type DateRangeType = '1d' | '1w' | '1m' | '7d' | '15d' | '30d' | '180d' | '365d' | 'custom';

export type DateRangeAmountType = 'day' | 'week' | 'month' | 'days';

export type DateRangeOnChange = (startDate: Moment, endDate: Moment, range: DateRangeType) => void;

export function calcRangeTypeByDates(
  startDate: Moment,
  endDate: Moment,
  amountType?: DateRangeAmountType
): DateRangeType {
  const DaysRangeMap: Record<number, DateRangeType> = {
    1: '1d',
    6: '7d',
    14: '15d',
    29: '30d',
    179: '180d',
    364: '365d',
  };
  const actualAmountType = amountType ?? calcAmountType(startDate, endDate);

  switch (actualAmountType) {
    case 'day':
      return '1d';
    case 'week':
      return '1w';
    case 'month':
      return '1m';
    default: {
      const daysDiff = endDate.diff(startDate, 'days');

      return DaysRangeMap[daysDiff] || 'custom';
    }
  }
}

export function calcDatesByRange(
  startDate: Moment,
  endDate: Moment,
  range: DateRangeType
): {startDate: Moment; endDate: Moment} {
  const RangeDaysMap: Record<DateRangeType, number> = {
    custom: -1,
    '1d': 0,
    '1w': -2,
    '1m': -3,
    '7d': 6,
    '15d': 14,
    '30d': 29,
    '180d': 179,
    '365d': 364,
  };

  const days = RangeDaysMap[range];

  // Custom range
  if (days === -1) {
    return {startDate: startDate.clone().startOf('day'), endDate: endDate.clone().endOf('day')};
  }

  // 1 week range
  if (days === -2) {
    return {startDate: endDate.clone().startOf('week'), endDate: endDate.clone().endOf('week')};
  }

  // 1 month range
  if (days === -3) {
    return {startDate: endDate.clone().startOf('month'), endDate: endDate.clone().endOf('month')};
  }

  return {
    startDate: endDate.clone().subtract(days, 'days').startOf('day'),
    endDate: endDate.clone().endOf('day'),
  };
}

export function calcNewDates(
  startDate: Moment,
  endDate: Moment,
  method: 'add' | 'subtract'
): {startDate: Moment; endDate: Moment} {
  const amountType = calcAmountType(startDate, endDate);
  const amount = calcAmount(startDate, endDate, amountType);
  const newStartDate = calcDateByAmount(startDate, amount, amountType, method);
  const newEndDate = calcDateByAmount(endDate, amount, amountType, method);

  switch (amountType) {
    case 'week':
      newStartDate.startOf('week');
      newEndDate.endOf('week');
      break;
    case 'month':
      newStartDate.startOf('month');
      newEndDate.endOf('month');
      break;
    case 'day':
    case 'days':
    default:
      newStartDate.startOf('day');
      newEndDate.endOf('day');
      break;
  }

  return {startDate: newStartDate, endDate: newEndDate};
}

export function calcDateByAmount(
  date: Moment,
  amount: number,
  amountType: DateRangeAmountType,
  method: 'add' | 'subtract'
): Moment {
  return date.clone()[method](amount, amountType);
}

export function calcAmountType(startDate: Moment, endDate: Moment): DateRangeAmountType {
  // Check if same day
  const sameDay = startDate.format('YYYY-MM-DD') === endDate.format('YYYY-MM-DD');
  if (sameDay) {
    return 'day';
  }

  // Check if same week
  const startDateWeek = startDate.clone().startOf('week');
  const endDateWeek = endDate.clone().startOf('week');
  const sameWeek = startDateWeek.isSame(endDateWeek, 'day');
  if (sameWeek) {
    return 'week';
  }

  // Check if same month
  const firstDayOfTheMonth = startDate.clone().startOf('month');
  const lastDayOfTheMonth = endDate.clone().endOf('month');

  if (
    startDate.isSame(firstDayOfTheMonth, 'day') &&
    endDate.isSame(lastDayOfTheMonth, 'day') &&
    startDate.isSame(endDate, 'year')
  ) {
    return 'month';
  }

  // Default to days
  return 'days';
}

export function calcAmount(
  startDate: Moment,
  endDate: Moment,
  amountType: DateRangeAmountType
): number {
  switch (amountType) {
    case 'day':
    case 'week':
    case 'month':
      return 1;
    case 'days':
      return endDate.diff(startDate, 'days');
    default:
      throw new Error('Invalid range date amount type. Missing implementation!');
  }
}

export function calcStartDateByRange(range: DateRangeType, givenDate?: Moment): Moment {
  const date = givenDate ? givenDate.clone().utc() : moment().utc();

  switch (range) {
    case '1d':
      return date.startOf('day');
    case '1w':
      return date.startOf('week');
    case '1m':
      return date.startOf('month');
    case '7d':
      return date.subtract(6, 'days').startOf('day');
    case '15d':
      return date.subtract(14, 'days').startOf('day');
    case '30d':
      return date.subtract(29, 'days').startOf('day');
    case '180d':
      return date.subtract(179, 'days').startOf('day');
    case '365d':
      return date.subtract(364, 'days').startOf('day');
    default:
      return date.subtract(6, 'days').startOf('day');
  }
}

export function calcEndDateByRange(range: DateRangeType, givenDate?: Moment): Moment {
  const date = givenDate ? givenDate.clone().utc() : moment().utc();

  switch (range) {
    case '1d':
    case '7d':
    case '15d':
    case '30d':
    case '180d':
    case '365d':
      return date.endOf('day');
    case '1w':
      return date.endOf('week');
    case '1m':
      return date.endOf('month');
    default:
      return date.endOf('day');
  }
}
